/**
 * Shared component for donation preferences fields
 * Used in registration and profile forms
 */
import React from 'react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { BLOOD_TYPES, ORGANS } from '../../mockData';

const DonationPreferencesFields = ({
  formData,
  onChange,
  selectedOrgans = [],
  onOrganToggle,
  errors = {},
  showBloodType = true,
  showDonationType = true,
  showOrgansSelection = true,
  showEmergencyContact = true,
  onBlur = null
}) => {
  return (
    <>
      {showBloodType && (
        <div>
          <Label htmlFor="blood-type" className="text-gray-900 font-semibold mb-2 block">
            Blood Type
          </Label>
          <Select
            value={formData.bloodType || ''}
            onValueChange={(value) => onChange('bloodType', value)}
          >
            <SelectTrigger className="border-gray-300 h-12">
              <SelectValue placeholder="Select your blood type" />
            </SelectTrigger>
            <SelectContent>
              {BLOOD_TYPES.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.bloodType && <p className="text-red-600 text-sm mt-1">{errors.bloodType}</p>}
        </div>
      )}

      <div className="border-t pt-4">
        <h2 className="font-bold text-gray-900 mb-3 text-lg">Donation Preferences</h2>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-blood-donor"
              checked={formData.isBloodDonor || false}
              onCheckedChange={(checked) => onChange('isBloodDonor', checked)}
            />
            <Label htmlFor="is-blood-donor" className="text-gray-900 font-normal">
              I want to be a Blood Donor
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-organ-donor"
              checked={formData.isOrganDonor || false}
              onCheckedChange={(checked) => onChange('isOrganDonor', checked)}
            />
            <Label htmlFor="is-organ-donor" className="text-gray-900 font-normal">
              I want to be an Organ Donor
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="body-donation"
              checked={formData.bodyDonation || false}
              onCheckedChange={(checked) => onChange('bodyDonation', checked)}
            />
            <Label htmlFor="body-donation" className="text-gray-900 font-normal">
              Body Donation
            </Label>
          </div>
        </div>
      </div>

      {showDonationType && formData.isOrganDonor && (
        <div className="mt-4">
          <Label className="text-gray-900 font-semibold mb-2 block">Donation Type</Label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onChange('donationType', 'living')}
              className={`flex-1 py-2 px-4 rounded-md border-2 transition-colors ${
                formData.donationType === 'living'
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-red-300'
              }`}
            >
              Living
            </button>
            <button
              type="button"
              onClick={() => onChange('donationType', 'deceased')}
              className={`flex-1 py-2 px-4 rounded-md border-2 transition-colors ${
                formData.donationType === 'deceased'
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-red-300'
              }`}
            >
              Deceased
            </button>
            <button
              type="button"
              onClick={() => onChange('donationType', 'both')}
              className={`flex-1 py-2 px-4 rounded-md border-2 transition-colors ${
                formData.donationType === 'both'
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-red-300'
              }`}
            >
              Both
            </button>
          </div>
        </div>
      )}

      {showOrgansSelection && formData.isOrganDonor && (
        <div className="mt-4">
          <Label className="text-gray-900 mb-3 block">Organs Pledged <span className="text-red-700">*</span></Label>
          <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
            {ORGANS.map((organ) => (
              <div key={organ.id} className="flex items-start space-x-3">
                <Checkbox
                  id={`organ-pledge-${organ.id}`}
                  checked={selectedOrgans.includes(organ.id)}
                  onCheckedChange={() => {
                    onOrganToggle(organ.id);
                    if (onBlur) {
                      // Trigger validation after organ toggle
                      setTimeout(() => {
                        const updatedOrgans = selectedOrgans.includes(organ.id)
                          ? selectedOrgans.filter(id => id !== organ.id)
                          : [...selectedOrgans, organ.id];
                        onBlur('organs', updatedOrgans);
                      }, 0);
                    }
                  }}
                />
                <div className="flex-1">
                  <Label htmlFor={`organ-pledge-${organ.id}`} className="text-gray-900 font-medium cursor-pointer">
                    {organ.name}
                  </Label>
                  {organ.livingDonation && (
                    <p className="text-xs text-gray-600">Living donation possible</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {errors['organ-organs'] && <p className="text-red-600 text-sm mt-1">{errors['organ-organs']}</p>}
        </div>
      )}

      {showEmergencyContact && (
        <div className="border-t pt-4">
          <h2 className="font-bold text-gray-900 mb-3 text-lg">Emergency Contact <span className="text-red-700">*</span></h2>
          <div className="space-y-3">
            <div>
              <Label htmlFor="emergency-name" className="text-gray-900">Name <span className="text-red-700">*</span></Label>
              <Input
                id="emergency-name"
                name="emergency-name"
                value={formData.emergencyContact?.name || ''}
                onChange={(e) => onChange('emergencyContact', {
                  ...formData.emergencyContact,
                  name: e.target.value
                })}
                onBlur={(e) => {
                  if (onBlur) {
                    onBlur('emergencyContact', {
                      ...formData.emergencyContact,
                      name: e.target.value
                    });
                  }
                }}
                className="border-gray-300 h-12"
              />
              {errors['emergency-contact-name'] && <p className="text-red-600 text-sm mt-1">{errors['emergency-contact-name']}</p>}
            </div>
            <div>
              <Label htmlFor="emergency-phone" className="text-gray-900">Phone <span className="text-red-700">*</span></Label>
              <Input
                id="emergency-phone"
                name="emergency-phone"
                type="tel"
                value={formData.emergencyContact?.phone || ''}
                onChange={(e) => onChange('emergencyContact', {
                  ...formData.emergencyContact,
                  phone: e.target.value
                })}
                onBlur={(e) => {
                  if (onBlur) {
                    onBlur('emergencyContact', {
                      ...formData.emergencyContact,
                      phone: e.target.value
                    });
                  }
                }}
                className="border-gray-300 h-12"
              />
              {errors['emergency-contact-phone'] && <p className="text-red-600 text-sm mt-1">{errors['emergency-contact-phone']}</p>}
            </div>
            <div>
              <Label id="emergency-relationship-label" htmlFor="emergency-relationship" className="text-gray-900">Relationship</Label>
              <Select
                value={formData.emergencyContact?.relationship || 'Emergency Contact'}
                onValueChange={(value) => onChange('emergencyContact', {
                  ...formData.emergencyContact,
                  relationship: value
                })}
              >
                <SelectTrigger id="emergency-relationship" className="border-gray-300 h-12" aria-labelledby="emergency-relationship-label">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Spouse">Spouse</SelectItem>
                  <SelectItem value="Parent">Parent</SelectItem>
                  <SelectItem value="Child">Child</SelectItem>
                  <SelectItem value="Sibling">Sibling</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                  <SelectItem value="Emergency Contact">Emergency Contact</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {errors['organ-emergency-contact'] && <p className="text-red-600 text-sm mt-1">{errors['organ-emergency-contact']}</p>}
        </div>
      )}
    </>
  );
};

export default DonationPreferencesFields;

